import { Component, inject, OnDestroy, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { Router } from '@angular/router';
import { SharedModule } from '../../shared/shared.module';
import { AuthenticationService } from '../../core/service/auth/authentication.service';
import { LocalStorageService } from '../../core/service/localStorage/local-storage.service';
import { CoreSocketService } from '../../core/service/core-socket/core-socket.service';
import { MailCacheService } from '../../core/service/mailCache/mail-cache.service';
import { LoaderService } from '../../core/service/loader.service';
import { PopupServiceService } from '../../core/service/popup/popup-service.service';
import { TMSService, SignOffPayload, CalendarSelfReport, TaskSelfReport } from '../../core/service/tms/tms.service';
import moment from 'moment';
import { TmsSocketService } from '../../core/service/tms-socket/tms-socket.service';

// Work the agent has finished. 'completed' and 'resolved_not_reviewed' mean the
// task is done and sitting with the requester — not something to chase at sign-off.
const DONE_STATUSES = ['resolved', 'closed', 'completed', 'resolved_not_reviewed'];

export interface WrapUpTask {
  id: string;
  name: string;
  assignedBy: string;
  dueLabel: string;
  completed: boolean;
  note: string;
}

export interface WrapUpCalendarEvent {
  id: string;
  name: string;
  time: string;
  attendance: 'attended' | 'missed' | null;
  note: string;
}

@Component({
  selector: 'app-sign-off-wrapup',
  standalone: true,
  imports: [CommonModule, FormsModule, SharedModule],
  templateUrl: './sign-off-wrapup.component.html',
  styleUrl: './sign-off-wrapup.component.scss',
})
export class SignOffWrapupComponent implements OnInit, OnDestroy {
  private router = inject(Router);
  private auth = inject(AuthenticationService);
  private localStorageService = inject(LocalStorageService);
  private socketService = inject(CoreSocketService);
  private mailCacheService = inject(MailCacheService);
  private loader = inject(LoaderService);
  private popup = inject(PopupServiceService);
  private tmsService = inject(TMSService);
  private tmsSocketService = inject(TmsSocketService);

  tasks: WrapUpTask[] = [];
  tasksLoading = true;
  calendarEvents: WrapUpCalendarEvent[] = [];
  calendarLoading = true;
  currentUser: any;

  // True when this page is shown because the user forgot to sign off the
  // previous day and is catching up on next login — in that case we
  // continue into the normal app flow instead of logging the user out.
  isCatchUpSignOff = false;

  // True when the user reached this page via "End the Day" from the away-lock
  // screen — there's no dashboard to go back to in that flow, so the close
  // (skip sign-off) button doesn't apply.
  isFromLock = false;

  // The away-lock's End-the-Day choice leaves the TMS lock in effect (see
  // away-lock.component.ts) until sign-off actually completes — set true once
  // it does, so ngOnDestroy knows not to re-lock on the way out.
  private _signOffCompleted = false;

  constructor() {
    const nav = this.router.getCurrentNavigation();
    this.isCatchUpSignOff = !!nav?.extras?.state?.['isCatchUp'];
    this.isFromLock = !!nav?.extras?.state?.['fromLock'];
  }

  async ngOnInit(): Promise<void> {
    this.currentUser = await this.localStorageService.getKeyIndexedDbKey('currentUser');
    this.loadTasks();
    this.loadCalendarEvents();
  }

  ngOnDestroy(): void {
    // Left the wrap-up flow (browser back, refresh away, etc.) without actually
    // signing off — the user didn't choose to leave the away-lock state, so
    // re-show the lock screen instead of letting them land back in the app unlocked.
    if (this.isFromLock && !this._signOffCompleted) {
      this.tmsSocketService.showLockOverlay();
    }
  }

  // lastActiveDate is only sent when catching up on a missed sign-off
  // (isCatchUpSignOff) — otherwise the backend should use the current date.
  private get _lastActiveDate(): string | undefined {
    if (!this.isCatchUpSignOff) return undefined;
    return this.currentUser?.lastActiveDate
      ? moment(this.currentUser.lastActiveDate).format('YYYY-MM-DD')
      : undefined;
  }

  // Label for the catch-up day shown in the wrap-up copy — 'Yesterday' when
  // lastActiveDate really was the day before today, otherwise the actual
  // weekday name (e.g. 'Monday'), since lastActiveDate can be older than one day.
  get catchUpDayLabel(): string {
    const lastActiveDate = this.currentUser?.lastActiveDate;
    if (!lastActiveDate) return 'Yesterday';
    const lastActive = moment(lastActiveDate).startOf('day');
    const yesterday = moment().subtract(1, 'day').startOf('day');
    return lastActive.isSame(yesterday, 'day') ? 'Yesterday' : lastActive.format('dddd');
  }

   private loadTasks() {
    const today = moment().format('YYYY-MM-DD');
    this.tasksLoading = true;

    this.tmsService.getTasks(today, this.currentUser?.id, this._lastActiveDate).subscribe({
      next: (res: any) => {
        this.tasksLoading = false;
        const data = res?.data?.data || [];
        this.tasks = data.map((item: any) => ({
          id:         item?.id || '',
          name:       `[${item?.taskNumber}] — ${item?.title}`,
          assignedBy: item?.requesterName ? `Assigned by ${item.requesterName}` : 'Assigned by Self',
          dueLabel:   item?.resolutionDueAt
                        ? `Due Today ${moment(item.resolutionDueAt).format('hh:mm A')}`
                        : 'Due Today',
          completed:  DONE_STATUSES.includes(item?.status),
          note:       '',
        }));
      },
      error: () => {
        this.tasksLoading = false;
      },
    });
  }

  private loadCalendarEvents(): void {
    const today = moment().format('YYYY-MM-DD');
    this.calendarLoading = true;

    this.tmsService.getCalendarEvents(today, today, this._lastActiveDate).subscribe({
      next: (res: any) => {
        this.calendarLoading = false;
        const events = res?.data?.events || [];
        this.calendarEvents = events.map((item: any) => ({
          id:         item?.id || '',
          name:       item?.title || item?.summary || item?.name || '',
          time:       this._formatEventTime(item),
          attendance: null,
          note:       '',
        }));
      },
      error: () => {
        this.calendarLoading = false;
        // Silently fail — empty state shown
      },
    });
  }

  private _formatEventTime(item: any): string {
    const startRaw = item?.startTime || item?.start?.dateTime || item?.start;
    const endRaw   = item?.endTime   || item?.end?.dateTime   || item?.end;
    if (!startRaw) return '';
    const start = moment(startRaw).format('hh:mm A');
    const end   = endRaw ? moment(endRaw).format('hh:mm A') : '';
    return end ? `${start} – ${end}` : start;
  }

  get allAttendanceMarked(): boolean {
    if (this.calendarLoading) return false;
    if (this.calendarEvents.length === 0) return true;
    return this.calendarEvents.every(e => e.attendance !== null);
  }

  selectAllTasks(): void {
    this.tasks.forEach(t => t.completed = true);
  }

  setAttendance(event: WrapUpCalendarEvent, status: 'attended' | 'missed'): void {
    event.attendance = event.attendance === status ? null : status;
  }

  onClose(): void {
    this.router.navigate(['/dashboard/all-application']);
  }

  async onSignOff(): Promise<void> {
    const currentUser = await this.localStorageService.getKeyIndexedDbKey('currentUser');

    const payload: SignOffPayload = {
      userId: currentUser?.id,
    };

    // Only include calendarSelfReports if there are events
    if (this.calendarEvents.length > 0) {
      payload.calendarSelfReports = this.calendarEvents.map((event): CalendarSelfReport => ({
        eventId:          event.id,
        attendanceStatus: event.attendance === 'attended' ? 1 : 0,
        attendanceNote:   event.note?.trim() || '',
      }));
    }

    // Only include taskSelfReports if there are tasks
    if (this.tasks.length > 0) {
      payload.taskSelfReports = this.tasks.map((task): TaskSelfReport => ({
        taskId:      task.id,
        isCompleted: task.completed,
        taskNote:    task.note?.trim() || '',
      }));
    }

    this.loader.show();
    this.tmsService.signOff(payload).subscribe({
      next: () => {
        this.loader.hide();
        this._signOffCompleted = true;
        this.tmsSocketService.markUnlocked();
        if (this.isCatchUpSignOff) {
          this._goToStartDay();
        } else {
          this._doLogout();
        }
      },
      error: (errr) => {
        this.loader.hide();
        const msg = errr?.error?.message || '';
        this.popup.successAndErrorDialog('error', msg || 'Sign off failed. Please try again.');
      },
    });
  }

  // Catch-up flow: previous day's sign off is now resolved, but today's day
  // hasn't been started yet — send the user to start-day (it handles the
  // redirect/dashboard navigation itself once they sign in).
  private _goToStartDay(): void {
    this.router.navigate(['/start-day']);
  }

  private _doLogout(): void {
    this.auth.logout().subscribe({
      next: async (res: any) => {
        if (res?.data?.status) {
          // Signal other tabs to logout — must be before disconnect clears localStorage
          localStorage.setItem('tms_logout_signal', 'true');
          this.tmsService.reset();
          this.socketService.disconnect();
          this.tmsSocketService.disconnect();
          this.localStorageService.removeIndexedDbKey('currentUser');
          this.localStorageService.clearIndexedDb();
          this.localStorageService.removeIndexedDbKey('switchUser');
          this.localStorageService.removeKey('access_token');
          this.localStorageService.removeKey('refreshToken');
          this.localStorageService.removeKey('switchUserToken');
          this.localStorageService.removeKey('isSwitchUser');
          await this.mailCacheService.clearAllCache();
          this.router.navigate(['/login']);
        }
      },
      error: () => {
        this.popup.successAndErrorDialog('error', 'Logout failed. Please try again.');
      },
    });
  }
}